import { morph } from '@theme/morph';

/**
 * `<color-variant-picker>` swaps the PDP to the sibling color product in place, without a reload.
 *
 * Colors are modelled as SEPARATE products (metafield `custom.productos_relacionados`).
 * Clicking a swatch fetches the sibling's full page and morphs `<main>` — the same
 * mechanism the theme uses for combined listings (`variant-picker.js`). The URL is
 * updated via pushState, and `popstate` re-syncs on back/forward.
 *
 * Each swatch is an `<a>`: without JS it navigates normally (progressive enhancement).
 */
class ColorVariantPicker extends HTMLElement {
  /** @type {Map<string, Promise<Document>>} */
  #cache = new Map();
  #prefetched = false;
  /** Monotonic id of the latest swap request; guards against out-of-order resolves. */
  #swapSeq = 0;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerenter', this.#handlePrefetch);
    bindPopstateOnce();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerenter', this.#handlePrefetch);
  }

  /** Prefetches every swatch's page the first time the row is hovered. */
  #handlePrefetch = () => {
    if (this.#prefetched) return;
    this.#prefetched = true;

    for (const link of this.querySelectorAll('[data-swatch]')) {
      const url = link.getAttribute('data-url');
      if (url) this.#fetchPage(url).catch(() => {});
    }
  };

  /** @param {Event} event */
  #handleClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-swatch]') : null;
    if (!link) return;

    // Intercept the <a> navigation; with JS we swap in place.
    event.preventDefault();

    if (link.getAttribute('aria-current') === 'true') return;

    const url = link.getAttribute('data-url');
    if (url) this.#swap(url, true, true);
  };

  /**
   * The pathnames of every color this picker links to.
   * @returns {string[]}
   */
  #swatchPaths() {
    return Array.from(this.querySelectorAll('[data-swatch]'), (link) =>
      link.getAttribute('data-url')?.split('?')[0] ?? ''
    );
  }

  /**
   * Handles back/forward navigation. Re-morphs `<main>` only when the current URL is one
   * of this picker's colors and isn't the one already shown. This URL-based check is
   * robust even when another feature (e.g. variant-picker) has overwritten history.state.
   */
  handleLocationChange() {
    const path = window.location.pathname;

    const active = this.querySelector('[data-swatch][aria-current="true"]');
    const activePath = active?.getAttribute('data-url')?.split('?')[0];
    if (activePath === path) return; // already showing this color

    if (this.#swatchPaths().includes(path)) this.swapToCurrentLocation();
  }

  /**
   * Fetches (and caches) the sibling product's full page.
   * @param {string} pageUrl
   * @returns {Promise<Document>}
   */
  #fetchPage(pageUrl) {
    const key = pageUrl.split('?')[0];
    const cached = this.#cache.get(key);
    if (cached) return cached;

    const request = fetch(key)
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        return response.text();
      })
      .then((text) => new DOMParser().parseFromString(text, 'text/html'))
      .catch((error) => {
        // Don't poison the cache with a rejected request.
        this.#cache.delete(key);
        throw error;
      });

    this.#cache.set(key, request);
    return request;
  }

  /**
   * Fetches the sibling page and morphs `<main>`.
   * @param {string} pageUrl
   * @param {boolean} pushUrl - When true, adds the new URL to history.
   * @param {boolean} [focusActive] - When true, move focus to the new active swatch after the swap.
   */
  async #swap(pageUrl, pushUrl, focusActive = false) {
    const main = document.querySelector('main');
    if (!main) return;

    const seq = ++this.#swapSeq;
    this.setAttribute('aria-busy', 'true');

    try {
      const doc = await this.#fetchPage(pageUrl);

      // A newer swap started while this one was in flight — drop this stale result.
      if (seq !== this.#swapSeq) return;

      const newMain = doc.querySelector('main');
      if (!newMain) throw new Error('No <main> in response');

      // `morph` moves and deletes nodes out of the tree it reads from, so it gets a copy:
      // `doc` lives in `#cache` and has to stay intact for the next swap to this color.
      // Without this, the second swap to a color morphs against a half-emptied document
      // and the missing nodes (add to cart button, sticky bar) drop out of the page.
      //
      // `cloneNode` keeps the copy inside `doc`, which `DOMParser` built without a browsing
      // context, so its custom elements stay un-upgraded — morph calls `updatedCallback()` on
      // the nodes it reads, and on a gutted throwaway tree those throw MissingRefError.
      // `document.importNode` would upgrade them; only nodes morph actually inserts should be.
      morph(main, newMain.cloneNode(true));

      this.#replaceVariantPicker(main, newMain);

      // The row was re-rendered by the morph; allow it to prefetch again.
      this.#prefetched = false;

      if (pushUrl) {
        const cleanUrl = pageUrl.split('?')[0];
        history.pushState({}, '', cleanUrl);
      }

      if (focusActive) {
        const active = this.querySelector('[data-swatch][aria-current="true"]');
        if (active instanceof HTMLElement) active.focus({ preventScroll: true });
      }
    } catch (error) {
      // Theme convention: warn, don't throw. Hard fallback: navigate for real — but only
      // for the latest swap, so a stale failed request can't clobber a newer success.
      console.warn('[color-variant-picker] swap failed:', error);
      if (seq === this.#swapSeq) window.location.assign(pageUrl);
    } finally {
      // Only the latest swap owns the busy state.
      if (seq === this.#swapSeq) this.removeAttribute('aria-busy');
    }
  }

  /**
   * Rebuilds the variant picker from the incoming markup instead of letting the morph
   * reconcile it, because morphing one product's picker into another's corrupts it:
   *
   * - The radios are named `<option>-<block id>-<product id>`, so a swap renames every
   *   radio group one input at a time. Mid-rename the outgoing and incoming selections
   *   briefly share a group, the browser's radio-group normalization drops one of them,
   *   and the picker ends up with no size selected at all.
   * - `data-current-checked` / `data-previous-checked` drive the selected pill in CSS and
   *   are in the morph's preserved-attribute list, so the previous product's sizes stay
   *   highlighted on top of the new product's — several sizes read as selected at once.
   * - `<variant-picker>` derives its selected/previous indices in `connectedCallback`,
   *   which a morphed element never re-runs.
   *
   * Inserting a fresh node re-initializes the element cleanly, the same way
   * `color-swatches.js` replaces the slideshow on a card swap.
   *
   * @param {Element} main - The live `<main>`, already morphed.
   * @param {Element} newMain - The fetched product's `<main>` (untouched by the morph).
   */
  #replaceVariantPicker(main, newMain) {
    const live = main.querySelector('variant-picker[ref="mainVariantPicker"]');
    const next = newMain.querySelector('variant-picker[ref="mainVariantPicker"]');

    if (live && next) live.replaceWith(document.importNode(next, true));
  }

  /** Re-syncs the DOM with the current URL (used by popstate). */
  swapToCurrentLocation() {
    this.#swap(window.location.href, false).catch(() => {});
  }
}

let popstateBound = false;

/**
 * Binds a single global `popstate` listener so back/forward between colors re-morph
 * `<main>`. The picker decides (by URL) whether the entry is one of its colors.
 */
function bindPopstateOnce() {
  if (popstateBound) return;
  popstateBound = true;

  window.addEventListener('popstate', () => {
    const picker = document.querySelector('color-variant-picker');
    if (picker instanceof ColorVariantPicker) {
      picker.handleLocationChange();
    }
  });
}

if (!customElements.get('color-variant-picker')) {
  customElements.define('color-variant-picker', ColorVariantPicker);
}
